import { useEffect, useState, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
         LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";
import api from "../api/client";

const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const d = new Date(); d.setMonth(d.getMonth() - i);
  return d.toISOString().slice(0, 7);
});

const PIE_COLORS = ["#2563eb","#059669","#b45309","#dc2626","#7c3aed","#0284c7","#c2410c","#059669","#a21caf","#a16207"];

const CSS = `
.rp-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:24px; flex-wrap:wrap; gap:12px; }
.rp-title  { font-size:18px; font-weight:700; color:#0d1a36; }
.rp-select { background:#f4f8ff; border:1px solid #c0d0eb; border-radius:7px; padding:8px 12px;
  font-size:13px; color:#0d1a36; outline:none; font-family:inherit; cursor:pointer; }
.rp-select:focus { border-color:#059669; }

.rp-metrics { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:16px; }
@media(max-width:600px){ .rp-metrics{ grid-template-columns:1fr; } }
.rp-metric { background:#ffffff; border:1px solid #c0d0eb; border-radius:10px; padding:18px 20px; }
.rp-metric-label { font-size:11px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:#6080aa; margin-bottom:6px; }
.rp-metric-value { font-size:22px; font-weight:700; color:#0d1a36; font-variant-numeric:tabular-nums; }
.rp-metric-delta { font-size:11px; margin-top:4px; }
.rp-delta-up   { color:#dc2626; }
.rp-delta-down { color:#059669; }

.rp-card { background:#ffffff; border:1px solid #c0d0eb; border-radius:10px; padding:24px; margin-bottom:14px; }
.rp-card-title { font-size:13px; font-weight:600; color:#0d1a36; margin-bottom:16px; }
.rp-row2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
@media(max-width:700px){ .rp-row2{ grid-template-columns:1fr; } }

.rp-table-wrap { overflow-x:auto; }
.rp-table { width:100%; border-collapse:collapse; }
.rp-table th { font-size:11px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:#6080aa;
  padding:0 12px 10px; text-align:left; border-bottom:1px solid #c0d0eb; }
.rp-table td { padding:9px 12px; border-bottom:1px solid #d5e3f5; font-size:13px; }
.rp-table tr:last-child td { border-bottom:none; }
.rp-table tr:hover td { background:#f4f8ff; }
.rp-td-num { color:#2563eb; font-weight:600; font-variant-numeric:tabular-nums; }
.rp-td-pct { color:#6080aa; }
.rp-td-status-ok   { color:#059669; font-weight:600; }
.rp-td-status-warn { color:#b45309; font-weight:600; }
.rp-td-status-over { color:#dc2626; font-weight:600; }

/* Calendar */
.rp-cal { border-collapse:separate; border-spacing:3px; width:100%; }
.rp-cal th { text-align:center; font-size:11px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; color:#6080aa; padding:4px; }
.rp-cal td { text-align:center; border-radius:6px; padding:7px 4px; font-size:12px; min-width:36px; }
.cal-empty { }
.cal-day   { background:#f4f8ff; color:#6080aa; }
.cal-low   { background:#dbeafe; color:#2563eb; font-weight:600; }
.cal-mid   { background:#1d4ed8; color:#fff; font-weight:700; }
.cal-high  { background:#1e40af; color:#fff; font-weight:700; border:1px solid #2563eb; }
.cal-amt   { font-size:10px; display:block; margin-top:2px; }

/* Budget vs actual */
.rp-bva-row { margin-bottom:12px; }
.rp-bva-header { display:flex; justify-content:space-between; margin-bottom:6px; font-size:13px; }
.rp-bva-cat  { color:#0d1a36; font-weight:600; }
.rp-bva-pct  { font-weight:600; }
.rp-bva-track { height:8px; background:#f4f8ff; border-radius:4px; overflow:hidden; margin-bottom:4px; }
.rp-bva-fill  { height:100%; border-radius:4px; }
.rp-bva-sub   { display:flex; justify-content:space-between; font-size:11px; color:#6080aa; }

/* Export */
.rp-export-row { display:flex; gap:10px; flex-wrap:wrap; }
.rp-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 18px; border-radius:7px;
  border:none; cursor:pointer; font-size:13px; font-weight:600; transition:opacity .15s; font-family:inherit; }
.rp-btn:hover { opacity:.88; }
.rp-btn-csv  { background:#059669; color:#f0fdf4; }
.rp-btn-json { background:#2563eb; color:#fff; }

.rp-empty { font-size:13px; color:#6080aa; }
.rp-spinner-wrap { display:flex; justify-content:center; align-items:center; height:200px; }
.rp-spinner { width:32px; height:32px; border:3px solid #c0d0eb; border-top-color:#059669; border-radius:50%; animation:rpspin .6s linear infinite; }
@keyframes rpspin { to{ transform:rotate(360deg); } }
`;

function buildCalendar(month, dailyData) {
  const spendMap = {};
  dailyData.forEach(d => {
    const key = String(d.date).slice(0,10);
    spendMap[key] = (spendMap[key] || 0) + parseFloat(d.total);
  });
  const [y, m] = month.split("-").map(Number);
  const firstDay = new Date(y, m-1, 1).getDay(); // 0=Sun
  const adjusted = (firstDay + 6) % 7; // Mon=0
  const daysInMonth = new Date(y, m, 0).getDate();
  const maxSpend = Math.max(...Object.values(spendMap), 1);

  const days = [];
  for (let i = 0; i < adjusted; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i+7));

  return { weeks, spendMap, maxSpend, month };
}

export default function Reports() {
  const [month,    setMonth]    = useState(new Date().toISOString().slice(0, 7));
  const [summary,  setSummary]  = useState([]);
  const [totalSpentReceipts,     setTotalSpentReceipts]     = useState([]);
  const [prevTotalSpentReceipts, setPrevTotalSpentReceipts] = useState([]);
  const [daily,    setDaily]    = useState([]);
  const [trend,    setTrend]    = useState([]);
  const [shopData, setShopData] = useState([]);
  const [budgets,  setBudgets]  = useState([]);
  const [monthly,  setMonthly]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  const prevMonth = MONTHS.find(m => m < month) || "";

  const load = useCallback(() => {
    setLoading(true);
    const calls = [
      api.get(`/expense/summary?month=${month}`),
      api.get(`/expense/total-spent?month=${month}`),
      api.get(`/expense/daily-spending?month=${month}`),
      api.get("/expense/category-trend"),
      api.get(`/expense/shop-spending?month=${month}`),
      api.get(`/expense/budgets?month=${month}`),
      api.get("/expense/monthly-totals"),
    ];
    if (prevMonth) calls.push(api.get(`/expense/total-spent?month=${prevMonth}`));

    Promise.all(calls).then(([s, tsr, d, ct, sh, b, mo, ptsr]) => {
      setSummary(s.data); setTotalSpentReceipts(tsr.data); setDaily(d.data); setTrend(ct.data);
      setShopData(sh.data); setBudgets(b.data); setMonthly(mo.data);
      if (ptsr) setPrevTotalSpentReceipts(ptsr.data);
    }).catch(()=>{}).finally(()=>setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const totalItems = summary.reduce((a,r)=>a+parseInt(r.item_count||0),0);
  const topCat     = summary[0]?.category || "—";

  const spentByCurrency = {};
  summary.forEach(r => {
    const c = r.currency || "OMR";
    spentByCurrency[c] = (spentByCurrency[c] || 0) + parseFloat(r.total);
  });
  // Receipt-level total (includes VAT) — used for the "Total Spent" metric specifically.
  const vatSpentByCurrency = {};
  totalSpentReceipts.forEach(r => {
    const c = r.currency || "OMR";
    vatSpentByCurrency[c] = (vatSpentByCurrency[c] || 0) + parseFloat(r.total);
  });
  const prevVatSpentByCurrency = {};
  prevTotalSpentReceipts.forEach(r => {
    const c = r.currency || "OMR";
    prevVatSpentByCurrency[c] = (prevVatSpentByCurrency[c] || 0) + parseFloat(r.total);
  });
  const vatSpendCurrencies = Object.keys(vatSpentByCurrency);

  const tt = { background:"#ffffff", border:"1px solid #c0d0eb", color:"#0d1a36" };

  // Category trend: pivot to recharts format
  const catTrendPivot = (() => {
    const map = {};
    trend.forEach(r => {
      if (!map[r.month]) map[r.month] = { month: r.month };
      map[r.month][r.category] = (map[r.month][r.category] || 0) + parseFloat(r.total);
    });
    return Object.values(map).sort((a,b)=>a.month.localeCompare(b.month)).slice(-6);
  })();
  const catKeys = [...new Set(trend.map(r=>r.category))];

  // Budget vs actual (keyed by category+currency so mixed-currency categories don't overwrite each other)
  const actualMap = {};
  summary.forEach(s => {
    const key = `${s.category}|${s.currency||"OMR"}`;
    actualMap[key] = (actualMap[key] || 0) + parseFloat(s.total);
  });

  // Calendar
  const cal = daily.length ? buildCalendar(month, daily) : null;

  // Build export rows (all items for the month via summary + shop data)
  const exportRows = () => {
    const rows = [["Category","Currency","Total","Items","Share %"]];
    summary.forEach(s => {
      const currency = s.currency || "OMR";
      const currencyTotal = spentByCurrency[currency] || 0;
      const share = currencyTotal > 0 ? (parseFloat(s.total)/currencyTotal*100).toFixed(1) : 0;
      rows.push([s.category, currency, parseFloat(s.total).toFixed(3), s.item_count||0, share]);
    });
    return rows;
  };

  const exportCSV = () => {
    if (!summary.length) return;
    const csv = exportRows().map(r=>r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv," + encodeURIComponent(csv);
    a.download = `expenses_${month}.csv`;
    a.click();
  };

  const exportExcel = () => {
    if (!summary.length) return;
    const rows = exportRows();
    // Build minimal XLSX using a data: URI with TSV inside an HTML table wrapped in Excel XML
    let xml = `<?xml version="1.0"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
      xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
      <Worksheet ss:Name="Expenses ${month}"><Table>`;
    rows.forEach((row, ri) => {
      xml += "<Row>";
      row.forEach(cell => {
        const isNum = ri > 0 && (typeof cell === "number" || (!isNaN(cell) && cell !== ""));
        xml += isNum
          ? `<Cell><Data ss:Type="Number">${cell}</Data></Cell>`
          : `<Cell><Data ss:Type="String">${String(cell).replace(/&/g,"&amp;").replace(/</g,"&lt;")}</Data></Cell>`;
      });
      xml += "</Row>";
    });
    xml += "</Table></Worksheet></Workbook>";
    const a = document.createElement("a");
    a.href = "data:application/vnd.ms-excel," + encodeURIComponent(xml);
    a.download = `expenses_${month}.xls`;
    a.click();
  };

  const exportPDF = async () => {
    if (!summary.length) return;
    // Fetch full item list for the month
    let items = [];
    try {
      const res = await api.get(`/expense/items?month=${month}`);
      items = res.data || [];
    } catch {}

    const now = new Date().toLocaleDateString("en-GB");

    const catRows = summary.map((s, i) => {
      const currency = s.currency || "OMR";
      const currencyTotal = spentByCurrency[currency] || 0;
      const share = currencyTotal > 0 ? (parseFloat(s.total)/currencyTotal*100).toFixed(1) : 0;
      const bg = i%2===0 ? "#f8fafc" : "#ffffff";
      return `<tr style="background:${bg}">
        <td>${s.category}</td>
        <td style="text-align:right;font-weight:600">${currency} ${parseFloat(s.total).toFixed(3)}</td>
        <td style="text-align:center">${s.item_count||0}</td>
        <td style="text-align:right">${share}%</td>
      </tr>`;
    }).join("");

    const itemRows = items.map((it, i) => {
      const currency = it.currency || "OMR";
      const bg = i%2===0 ? "#f8fafc" : "#ffffff";
      return `<tr style="background:${bg}">
        <td>${String(it.receipt_date||"").slice(0,10)}</td>
        <td>${it.shop_name||""}</td>
        <td>${it.item_name||""}</td>
        <td style="text-align:center">${it.quantity||1}</td>
        <td style="text-align:right">${currency} ${parseFloat(it.unit_price||0).toFixed(3)}</td>
        <td style="text-align:right;font-weight:600">${currency} ${parseFloat(it.total_price||0).toFixed(3)}</td>
        <td>${it.category||""}</td>
      </tr>`;
    }).join("");

    const shopRows = shopData.map((s, i) => {
      const bg = i%2===0 ? "#f8fafc" : "#ffffff";
      return `<tr style="background:${bg}">
        <td>${s.shop_name}</td>
        <td style="text-align:right;font-weight:600">${s.currency||"OMR"} ${parseFloat(s.total).toFixed(3)}</td>
        <td style="text-align:center">${s.visits||s.visit_count||"—"}</td>
      </tr>`;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>Expense Report — ${month}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:Arial,sans-serif;font-size:12px;color:#f4f8ff;padding:24px 32px;}
        .header{border-bottom:2px solid #1e40af;padding-bottom:12px;margin-bottom:20px;}
        .header h1{font-size:20px;color:#1e40af;margin-bottom:2px;}
        .header p{color:#475569;font-size:11px;}
        .metrics{display:flex;gap:16px;margin-bottom:20px;}
        .metric{flex:1;background:#eff6ff;border-radius:8px;padding:12px;text-align:center;}
        .metric-label{font-size:10px;color:#475569;font-weight:600;text-transform:uppercase;letter-spacing:.05em;margin-bottom:4px;}
        .metric-value{font-size:16px;font-weight:700;color:#1e40af;}
        h2{font-size:13px;font-weight:700;color:#f4f8ff;margin:18px 0 8px;border-left:3px solid #1e40af;padding-left:8px;}
        table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:8px;}
        th{background:#f4f8ff;color:#fff;padding:7px 10px;text-align:left;font-size:10px;letter-spacing:.05em;text-transform:uppercase;}
        td{padding:6px 10px;border-bottom:1px solid #e2e8f0;}
        .footer{margin-top:24px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:10px;color:#475569;text-align:center;}
        @page{margin:15mm;}
        @media print{.metric{background:#eff6ff!important;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
          th{background:#f4f8ff!important;color:#fff!important;-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
      </style></head><body>
      <div class="header">
        <h1>📊 Expense Report — ${month}</h1>
        <p>Generated on ${now} &nbsp;·&nbsp; ${items.length} items &nbsp;·&nbsp; ${summary.length} categories</p>
      </div>

      <div class="metrics">
        <div class="metric"><div class="metric-label">Total Spent</div><div class="metric-value">${vatSpendCurrencies.length ? vatSpendCurrencies.map(c=>`${c} ${vatSpentByCurrency[c].toFixed(3)}`).join("<br>") : "OMR 0.000"}</div></div>
        <div class="metric"><div class="metric-label">Items Purchased</div><div class="metric-value">${items.length}</div></div>
        <div class="metric"><div class="metric-label">Shops Visited</div><div class="metric-value">${shopData.length}</div></div>
        <div class="metric"><div class="metric-label">Top Category</div><div class="metric-value" style="font-size:13px">${summary[0]?.category||"—"}</div></div>
      </div>

      <h2>Category Breakdown</h2>
      <table><thead><tr><th>Category</th><th style="text-align:right">Total</th><th style="text-align:center">Items</th><th style="text-align:right">Share %</th></tr></thead>
      <tbody>${catRows}</tbody></table>

      ${shopData.length ? `<h2>Shop-wise Spending</h2>
      <table><thead><tr><th>Shop</th><th style="text-align:right">Total</th><th style="text-align:center">Visits</th></tr></thead>
      <tbody>${shopRows}</tbody></table>` : ""}

      ${items.length ? `<h2>All Expense Items</h2>
      <table><thead><tr><th>Date</th><th>Shop</th><th>Item</th><th style="text-align:center">Qty</th><th style="text-align:right">Unit Price</th><th style="text-align:right">Total</th><th>Category</th></tr></thead>
      <tbody>${itemRows}</tbody></table>` : ""}

      <div class="footer">Falcon Pro · Personal Expense Manager · ${month}</div>
      <script>window.onload=()=>{ setTimeout(()=>window.print(), 300); }<\/script>
      </body></html>`;

    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
  };

  if (loading) return <><style>{CSS}</style><div className="rp-spinner-wrap"><div className="rp-spinner"/></div></>;

  return (
    <>
      <style>{CSS}</style>
      <div className="rp-header">
        <div className="rp-title">📊 Reports</div>
        <select className="rp-select" value={month} onChange={e=>setMonth(e.target.value)}>
          {MONTHS.map(m=><option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {/* ── Summary Metrics ── */}
      <div className="rp-metrics">
        <div className="rp-metric">
          <div className="rp-metric-label">Total Spent</div>
          {vatSpendCurrencies.length === 0
            ? <div className="rp-metric-value">OMR 0.000</div>
            : vatSpendCurrencies.map(c => {
              const prev = prevVatSpentByCurrency[c];
              const deltaPct = prev > 0 ? ((vatSpentByCurrency[c]-prev)/prev*100) : null;
              return (
                <div key={c}>
                  <div className="rp-metric-value" style={{fontSize:vatSpendCurrencies.length>1?16:22}}>{c} {vatSpentByCurrency[c].toFixed(3)}</div>
                  {deltaPct !== null && (
                    <div className={`rp-metric-delta ${deltaPct>0?"rp-delta-up":"rp-delta-down"}`}>
                      {deltaPct>0?"▲":"▼"} {Math.abs(deltaPct).toFixed(1)}% vs {prevMonth}
                    </div>
                  )}
                </div>
              );
            })
          }
        </div>
        <div className="rp-metric">
          <div className="rp-metric-label">Items Purchased</div>
          <div className="rp-metric-value">{totalItems}</div>
        </div>
        <div className="rp-metric">
          <div className="rp-metric-label">Top Category</div>
          <div className="rp-metric-value" style={{fontSize:16}}>{topCat}</div>
          {summary[0] && <div className="rp-metric-delta" style={{color:'#6080aa'}}>{summary[0].currency||"OMR"} {parseFloat(summary[0].total).toFixed(3)}</div>}
        </div>
      </div>

      {/* ── Category Breakdown ── */}
      <div className="rp-card">
        <div className="rp-card-title">🗂️ Category Breakdown</div>
        {summary.length ? (
          <div className="rp-row2">
            <div className="rp-table-wrap">
              <table className="rp-table">
                <thead><tr><th>Category</th><th>Total</th><th>Items</th><th>Share</th></tr></thead>
                <tbody>
                  {summary.map(s => {
                    const currency = s.currency || "OMR";
                    const currencyTotal = spentByCurrency[currency] || 0;
                    const share = currencyTotal > 0 ? (parseFloat(s.total)/currencyTotal*100).toFixed(1) : 0;
                    return (
                      <tr key={`${s.category}-${currency}`}>
                        <td style={{color:'#0d1a36'}}>{s.category}</td>
                        <td className="rp-td-num">{currency} {parseFloat(s.total).toFixed(3)}</td>
                        <td style={{color:'#2a4a78'}}>{s.item_count||0}</td>
                        <td className="rp-td-pct">{share}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={summary.map(s=>({name:s.category,value:parseFloat(s.total),currency:s.currency||"OMR"}))}
                     dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80}>
                  {summary.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                </Pie>
                <Tooltip contentStyle={tt} formatter={(v,n,props)=>[`${props?.payload?.currency||"OMR"} ${parseFloat(v).toFixed(3)}`]} />
                <Legend iconSize={10} wrapperStyle={{fontSize:11,color:'#2a4a78'}} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : <div className="rp-empty">No expenses for {month}.</div>}
      </div>

      {/* ── Shop-wise Spending ── */}
      {shopData.length > 0 && (
        <div className="rp-card">
          <div className="rp-card-title">🏪 Shop-wise Spending</div>
          <div className="rp-table-wrap">
            <table className="rp-table">
              <thead><tr><th>Shop</th><th>Total</th><th>Visits</th></tr></thead>
              <tbody>
                {shopData.map(s=>(
                  <tr key={`${s.shop_name}-${s.currency||"OMR"}`}>
                    <td style={{color:'#0d1a36'}}>{s.shop_name}</td>
                    <td className="rp-td-num">{s.currency||"OMR"} {parseFloat(s.total).toFixed(3)}</td>
                    <td style={{color:'#2a4a78'}}>{s.visits||s.visit_count||"—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Monthly Trend ── */}
      <div className="rp-card">
        <div className="rp-card-title">📈 Monthly Trend</div>
        {monthly.length ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#c0d0eb" />
              <XAxis dataKey="month" stroke="#6080aa" tick={{fontSize:11}} />
              <YAxis stroke="#6080aa" tick={{fontSize:11}} />
              <Tooltip contentStyle={tt} formatter={(v,n,props)=>[`${props?.payload?.currency||"OMR"} ${parseFloat(v).toFixed(3)}`,"Spent"]} />
              <Bar dataKey="total" radius={[3,3,0,0]}
                   fill="#2563eb" />
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="rp-empty">No trend data yet.</div>}
      </div>

      {/* ── Category Comparison Across Months ── */}
      {catTrendPivot.length > 0 && (
        <div className="rp-card">
          <div className="rp-card-title">📊 Category Comparison Across Months</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={catTrendPivot}>
              <CartesianGrid strokeDasharray="3 3" stroke="#c0d0eb" />
              <XAxis dataKey="month" stroke="#6080aa" tick={{fontSize:11}} />
              <YAxis stroke="#6080aa" tick={{fontSize:11}} />
              <Tooltip contentStyle={tt} />
              <Legend wrapperStyle={{fontSize:11,color:'#2a4a78'}} />
              {catKeys.map((cat,i)=>(
                <Bar key={cat} dataKey={cat} stackId="a" fill={PIE_COLORS[i%PIE_COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Daily Spending Calendar ── */}
      {cal && (
        <div className="rp-card">
          <div className="rp-card-title">📆 Daily Spending — {month}</div>
          <div style={{overflowX:'auto'}}>
            <table className="rp-cal">
              <thead>
                <tr>{["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d=><th key={d}>{d}</th>)}</tr>
              </thead>
              <tbody>
                {cal.weeks.map((week,wi)=>(
                  <tr key={wi}>
                    {week.map((day,di)=>{
                      if (!day) return <td key={di} className="cal-empty"/>;
                      const key = `${month}-${String(day).padStart(2,"0")}`;
                      const spent = cal.spendMap[key] || 0;
                      const ratio = spent / cal.maxSpend;
                      const cls = spent===0 ? "cal-day" : ratio>=0.7 ? "cal-high" : ratio>=0.3 ? "cal-mid" : "cal-low";
                      return (
                        <td key={di} className={cls}>
                          {day}
                          {spent > 0 && <span className="cal-amt">{spent.toFixed(2)}</span>}
                        </td>
                      );
                    })}
                    {week.length < 7 && Array(7-week.length).fill(null).map((_,i)=><td key={`e${i}`} className="cal-empty"/>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{fontSize:11,color:'#6080aa',marginTop:10}}>🔵 Light = low spend &nbsp;|&nbsp; 🟦 Blue = medium &nbsp;|&nbsp; 💙 Dark = highest spending day</div>
        </div>
      )}

      {/* ── Budget vs Actual ── */}
      {budgets.length > 0 && (
        <div className="rp-card">
          <div className="rp-card-title">💰 Budget vs Actual</div>
          {budgets.map(b => {
            const cat      = b.category;
            const currency = b.currency || "OMR";
            const budget   = parseFloat(b.amount);
            const actual   = actualMap[`${cat}|${currency}`] || 0;
            const pctRaw  = budget > 0 ? (actual/budget*100) : 0;
            const pct     = Math.min(pctRaw, 100);
            const color   = pctRaw>=100 ? "#dc2626" : pctRaw>=75 ? "#b45309" : "#059669";
            const statusCls = pctRaw>=100 ? "rp-td-status-over" : pctRaw>=75 ? "rp-td-status-warn" : "rp-td-status-ok";
            const statusLbl = pctRaw>=100 ? "❌ Over" : pctRaw>=75 ? "⚠️ Warning" : "✅ On Track";
            return (
              <div className="rp-bva-row" key={`${cat}-${currency}`}>
                <div className="rp-bva-header">
                  <span className="rp-bva-cat">{cat}</span>
                  <span className={statusCls}>{statusLbl}</span>
                </div>
                <div className="rp-bva-track">
                  <div className="rp-bva-fill" style={{width:`${pct}%`,background:color}}/>
                </div>
                <div className="rp-bva-sub">
                  <span>Spent: {currency} {actual.toFixed(3)}</span>
                  <span>Budget: {currency} {budget.toFixed(3)}</span>
                  <span className="rp-bva-pct" style={{color}}>{pctRaw.toFixed(1)}% used</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Spending Heatmap ── */}
      {daily.length > 0 && (
        <div className="rp-card">
          <div className="rp-card-title">🔥 Spending Heatmap — Most Expensive Days</div>
          {(() => {
            const sorted = [...daily].sort((a,b)=>parseFloat(b.total)-parseFloat(a.total));
            const maxVal = parseFloat(sorted[0]?.total||1);
            const top3   = sorted.slice(0,3);
            const medals = ["🥇","🥈","🥉"];
            return (
              <>
                <div style={{overflowX:'auto'}}>
                  <div style={{display:'flex',gap:4,minWidth:'max-content',paddingBottom:8}}>
                    {daily.map(d=>{
                      const val   = parseFloat(d.total);
                      const ratio = val/maxVal;
                      const r=255, g=Math.round(160*(1-ratio)), b=50;
                      const day   = String(d.date).slice(8);
                      return (
                        <div key={`${d.date}-${d.currency||"OMR"}`} title={`${d.date}: ${d.currency||"OMR"} ${val.toFixed(3)}`}
                             style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                          <div style={{width:28,height:Math.max(20,ratio*120),borderRadius:'4px 4px 0 0',
                                       background:`rgba(${r},${g},${b},0.85)`,minHeight:20}}/>
                          <span style={{fontSize:10,color:'#6080aa'}}>{day}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div style={{fontSize:11,color:'#6080aa',marginBottom:12}}>
                  🟠 Light orange = small spend &nbsp;|&nbsp; 🔴 Dark red = highest spending day
                </div>
                <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
                  {top3.map((d,i)=>(
                    <div key={`${d.date}-${d.currency||"OMR"}`} style={{background:'#f4f8ff',borderRadius:8,padding:'10px 16px',fontSize:13}}>
                      <span style={{marginRight:6}}>{medals[i]}</span>
                      <strong style={{color:'#0d1a36'}}>{String(d.date).slice(5)}</strong>
                      <span style={{color:'#dc2626',marginLeft:8,fontWeight:700}}>
                        {d.currency||"OMR"} {parseFloat(d.total).toFixed(3)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ── Export ── */}
      <div className="rp-card">
        <div className="rp-card-title">⬇️ Export</div>
        {summary.length ? (
          <div className="rp-export-row">
            <button className="rp-btn rp-btn-csv" onClick={exportCSV}>📄 Export CSV</button>
            <button className="rp-btn rp-btn-json" onClick={exportExcel}>📊 Export Excel</button>
            <button className="rp-btn" style={{background:'#7c3aed',color:'#fff'}} onClick={exportPDF}>📋 Export PDF</button>
          </div>
        ) : <div className="rp-empty">No data to export for {month}.</div>}
      </div>
    </>
  );
}
